import React, { useState, useEffect } from 'react';
import { Brain, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Info } from 'lucide-react';

const StockAnalysis = ({ quote, historicalData, symbol }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (quote && historicalData && historicalData.length > 0) {
      setLoading(true);
      generateAnalysis(quote, historicalData, symbol);
    }
  }, [quote, historicalData, symbol]);

  const generateAnalysis = (quoteData, histData, stockSymbol) => {
    try {
      // Calculate technical indicators
      const prices = histData.map(d => d.close);
      const volumes = histData.map(d => d.volume);
      
      // Recent performance (last 5, 10, 20 days)
      const recent5 = prices.slice(-5);
      const recent10 = prices.slice(-10);
      const recent20 = prices.slice(-20);
      
      const performance5d = ((recent5[recent5.length - 1] - recent5[0]) / recent5[0] * 100).toFixed(2);
      const performance10d = ((recent10[recent10.length - 1] - recent10[0]) / recent10[0] * 100).toFixed(2);
      const performance20d = ((recent20[recent20.length - 1] - recent20[0]) / recent20[0] * 100).toFixed(2);
      
      // Moving averages
      const sma5 = recent5.reduce((a, b) => a + b, 0) / recent5.length;
      const sma10 = recent10.reduce((a, b) => a + b, 0) / recent10.length;
      const sma20 = recent20.reduce((a, b) => a + b, 0) / recent20.length;
      
      // Volatility (standard deviation of recent prices)
      const avgPrice = recent20.reduce((a, b) => a + b, 0) / recent20.length;
      const variance = recent20.reduce((sum, price) => sum + Math.pow(price - avgPrice, 2), 0) / recent20.length;
      const volatility = Math.sqrt(variance);
      
      // Volume analysis
      const avgVolume = volumes.slice(-20).reduce((a, b) => a + b, 0) / 20;
      const volumeRatio = quoteData.volume / avgVolume;
      
      // Price position analysis
      const highestPrice = Math.max(...prices);
      const lowestPrice = Math.min(...prices);
      const pricePosition = ((quoteData.price - lowestPrice) / (highestPrice - lowestPrice) * 100).toFixed(1);
      
      // Generate insights
      const insights = generateInsights({
        quote: quoteData,
        performance: { d5: performance5d, d10: performance10d, d20: performance20d },
        sma: { sma5, sma10, sma20 },
        volatility,
        volumeRatio,
        pricePosition,
        symbol: stockSymbol
      });
      
      const technicalAnalysis = generateTechnicalAnalysis({
        quote: quoteData,
        sma: { sma5, sma10, sma20 },
        volatility,
        volumeRatio,
        pricePosition
      });
      
      setAnalysis({
        insights,
        technicalAnalysis,
        metrics: {
          performance5d,
          performance10d,
          performance20d,
          volatility: volatility.toFixed(2),
          volumeRatio: volumeRatio.toFixed(2),
          pricePosition
        }
      });
      
    } catch (error) {
      console.error('Analysis error:', error);
      setAnalysis({
        error: 'Unable to generate analysis'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = ({ quote, performance, sma, volatility, volumeRatio, pricePosition, symbol }) => {
    const insights = [];
    
    // Price trend analysis
    if (quote.price > sma.sma5 && sma.sma5 > sma.sma10 && sma.sma10 > sma.sma20) {
      insights.push({
        type: 'bullish',
        title: 'Strong Uptrend',
        message: `${symbol} is showing a strong bullish trend with price above all moving averages.`,
        icon: TrendingUp
      });
    } else if (quote.price < sma.sma5 && sma.sma5 < sma.sma10 && sma.sma10 < sma.sma20) {
      insights.push({
        type: 'bearish',
        title: 'Downward Trend',
        message: `${symbol} is in a downtrend with price below all moving averages.`,
        icon: TrendingDown
      });
    } else {
      insights.push({
        type: 'neutral',
        title: 'Sideways Movement',
        message: `${symbol} is showing mixed signals with no clear trend direction.`,
        icon: Info
      });
    }
    
    // Performance analysis
    if (parseFloat(performance.d5) > 5) {
      insights.push({
        type: 'bullish',
        title: 'Strong Recent Performance',
        message: `Impressive ${performance.d5}% gain over the last 5 trading days indicates strong momentum.`,
        icon: CheckCircle
      });
    } else if (parseFloat(performance.d5) < -5) {
      insights.push({
        type: 'bearish',
        title: 'Recent Decline',
        message: `${performance.d5}% decline in the last 5 days suggests selling pressure.`,
        icon: AlertTriangle
      });
    }
    
    // Volume analysis
    if (volumeRatio > 1.5) {
      insights.push({
        type: 'bullish',
        title: 'High Trading Volume',
        message: `Trading volume is ${(volumeRatio * 100).toFixed(0)}% above average, indicating strong interest.`,
        icon: TrendingUp
      });
    } else if (volumeRatio < 0.5) {
      insights.push({
        type: 'neutral',
        title: 'Low Trading Volume',
        message: 'Below-average trading volume suggests limited market interest.',
        icon: Info
      });
    }
    
    // Price position analysis
    if (parseFloat(pricePosition) > 80) {
      insights.push({
        type: 'warning',
        title: 'Near High',
        message: `Trading at ${pricePosition}% of its range. Consider potential resistance levels.`,
        icon: AlertTriangle
      });
    } else if (parseFloat(pricePosition) < 20) {
      insights.push({
        type: 'opportunity',
        title: 'Near Low',
        message: `Trading at ${pricePosition}% of its range. Potential support level opportunity.`,
        icon: CheckCircle
      });
    }
    
    // Volatility analysis
    const volatilityPercent = (volatility / quote.price * 100);
    if (volatilityPercent > 3) {
      insights.push({
        type: 'warning',
        title: 'High Volatility',
        message: 'Stock shows high price volatility. Exercise caution with position sizing.',
        icon: AlertTriangle
      });
    }
    
    return insights;
  };

  const generateTechnicalAnalysis = ({ quote, sma, volatility, volumeRatio, pricePosition }) => {
    const signals = [];
    
    // Moving average signals
    if (quote.price > sma.sma20) {
      signals.push({ signal: 'BUY', strength: 'Strong', reason: 'Price above 20-day MA' });
    } else {
      signals.push({ signal: 'SELL', strength: 'Moderate', reason: 'Price below 20-day MA' });
    }
    
    // Volume confirmation
    if (volumeRatio > 1.2 && quote.change > 0) {
      signals.push({ signal: 'BUY', strength: 'Strong', reason: 'Volume-confirmed upward move' });
    } else if (volumeRatio > 1.2 && quote.change < 0) {
      signals.push({ signal: 'SELL', strength: 'Strong', reason: 'Volume-confirmed downward move' });
    }
    
    // RSI-like momentum
    const momentum = ((quote.price - sma.sma10) / sma.sma10 * 100);
    if (momentum > 5) {
      signals.push({ signal: 'OVERBOUGHT', strength: 'Moderate', reason: 'Price extended above MA' });
    } else if (momentum < -5) {
      signals.push({ signal: 'OVERSOLD', strength: 'Moderate', reason: 'Price extended below MA' });
    }
    
    return signals;
  };

  if (loading) {
    return (
      <div className="card loading-skeleton">
        <div className="skeleton-item" style={{height: '1.5rem', width: '40%', marginBottom: '1rem'}}></div>
        <div className="skeleton-item" style={{height: '4rem', marginBottom: '0.5rem'}}></div>
        <div className="skeleton-item" style={{height: '4rem', marginBottom: '0.5rem'}}></div>
        <div className="skeleton-item" style={{height: '4rem'}}></div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="empty-state">
        <Brain className="empty-state-icon" />
        <p>Select a stock to view AI analysis</p>
      </div>
    );
  }

  if (analysis.error) {
    return (
      <div className="error-state">
        <p>{analysis.error}</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="analysis-header">
        <h3 className="card-title">
          <Brain size={20} />
          Technical Analysis
        </h3>
        <div className="analysis-badge">
          Rule-Based Indicators
        </div>
      </div>

      {/* Key Insights */}
      <div className="analysis-section">
        <h4 className="section-title">Key Insights</h4>
        <div className="insights-grid">
          {analysis.insights.map((insight, index) => (
            <div key={index} className={`insight-card ${insight.type}`}>
              <div className="insight-header">
                <insight.icon size={16} />
                <span className="insight-title">{insight.title}</span>
              </div>
              <p className="insight-message">{insight.message}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Technical Signals */}
      <div className="analysis-section">
        <h4 className="section-title">Technical Signals</h4>
        <div className="signals-list">
          {analysis.technicalAnalysis.map((signal, index) => (
            <div key={index} className={`signal-item ${signal.signal.toLowerCase()}`}>
              <div className="signal-badge">
                {signal.signal}
              </div>
              <div className="signal-details">
                <span className="signal-strength">{signal.strength}</span>
                <span className="signal-reason">{signal.reason}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="analysis-section">
        <h4 className="section-title">Performance Metrics</h4>
        <div className="metrics-summary">
          <div className="metric-item">
            <span className="metric-label">5-Day Return</span>
            <span className={`metric-value ${parseFloat(analysis.metrics.performance5d) >= 0 ? 'positive' : 'negative'}`}>
              {analysis.metrics.performance5d}%
            </span>
          </div>
          <div className="metric-item">
            <span className="metric-label">10-Day Return</span>
            <span className={`metric-value ${parseFloat(analysis.metrics.performance10d) >= 0 ? 'positive' : 'negative'}`}>
              {analysis.metrics.performance10d}%
            </span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Price Position</span>
            <span className="metric-value">{analysis.metrics.pricePosition}%</span>
          </div>
          <div className="metric-item">
            <span className="metric-label">Volume Ratio</span>
            <span className="metric-value">{analysis.metrics.volumeRatio}x</span>
          </div>
        </div>
      </div>

      <div className="analysis-disclaimer">
        <Info size={14} />
        <span>This rule-based technical analysis uses traditional indicators and is for informational purposes only. Not investment advice.</span>
      </div>
    </div>
  );
};

export default StockAnalysis;